import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  FormControl,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import { toast } from "react-toastify";
import HlthtekFooterLogo from "../../assets/images/logo/NavLogo_new.png";

const INVOICE_PDF_FOOTER_TEXT =
  "Enhanced Patient Care Powered by Hlthtek Digital Healthcare Platform (visit: https://www.hlthtek.com)";

/** ISO 216 A5 portrait (mm). Passed explicitly so the PDF MediaBox is true A5, not A4. */
const INVOICE_PDF_A5_MM = [148, 210];

/**
 * When invoice payloads include overlapping line-item arrays (e.g. UI `itemsList` with empty
 * `itemName` vs API `items` with `item_name` on each row), pick the array with the most
 * resolved service names.
 */
function countResolvedServiceNames(rows) {
  if (!Array.isArray(rows)) return -1;
  let c = 0;
  for (const row of rows) {
    const raw =
      row?.item_name ??
      row?.itemName ??
      row?.service_name ??
      row?.name ??
      "";
    const t = String(raw).trim();
    if (t && t !== "--" && t !== "-") c += 1;
  }
  return c;
}

function pickInvoiceLineItems(data) {
  if (!data) return [];
  const candidates = [
    data.itemsList,
    data.invoice_items,
    data.invoiceItems,
    data.items,
  ].filter((a) => Array.isArray(a) && a.length > 0);
  if (candidates.length === 0) return [];
  let best = candidates[0];
  let bestScore = countResolvedServiceNames(best);
  for (let i = 1; i < candidates.length; i += 1) {
    const arr = candidates[i];
    const sc = countResolvedServiceNames(arr);
    if (sc > bestScore) {
      best = arr;
      bestScore = sc;
    }
  }
  return best;
}

function buildServiceNameByIdMap(serviceMaster = []) {
  const map = new Map();
  if (!Array.isArray(serviceMaster)) return map;
  serviceMaster.forEach((s) => {
    const key = s?.medical_service_id ?? s?.id;
    const value = s?.name ?? s?.item_name ?? "";
    if (key != null && String(value).trim()) {
      map.set(String(key), String(value).trim());
    }
  });
  return map;
}

function enrichLineItemsWithServiceNames(rows, serviceMap) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const existingName = String(
      row?.item_name ?? row?.itemName ?? row?.service_name ?? "",
    ).trim();
    if (existingName && existingName !== "--" && existingName !== "-") {
      return row;
    }
    const serviceId = row?.items ?? row?.item_id ?? row?.service_id;
    const resolved =
      serviceId != null ? serviceMap.get(String(serviceId)) || "" : "";
    if (!resolved) return row;
    return {
      ...row,
      item_name: resolved,
      itemName: resolved,
    };
  });
}

/**
 * jsPDF format for invoice PDFs. A5 uses explicit mm dimensions so exports are never A4-sized.
 * A4 keeps the string "a4" — behavior unchanged from before.
 */
function getInvoicePdfJsFormat(paperSize) {
  return paperSize === "a5" ? INVOICE_PDF_A5_MM : "a4";
}

function measureInvoicePdfFooter(doc, paperSize) {
  if (paperSize === "a5") {
    return {
      lines: [],
      lineH: 0,
      logoGap: 0,
      logoMaxH: 0,
      padTop: 0,
      padBot: 0,
      bandH: 0,
      narrow: true,
    };
  }
  const pageW = doc.internal.pageSize.getWidth();
  const textMaxW = Math.max(50, pageW - 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  const lines = doc.splitTextToSize(INVOICE_PDF_FOOTER_TEXT, textMaxW);
  const lineH = 3.5;
  const logoGap = 1.2;
  const logoMaxH = 7;
  const padTop = 2;
  const padBot = 2;
  // Text uses baselines at padTop+lineH .. padTop+lines.length*lineH; logo sits just below last baseline.
  const bandH = padTop + lines.length * lineH + logoGap + logoMaxH + padBot;
  return { lines, lineH, logoGap, logoMaxH, padTop, padBot, bandH, narrow: false };
}

function drawInvoicePdfFooter(doc, hlthtekLogoSrc, layout) {
  const pageHeight = doc.internal.pageSize.height;
  const pageW = doc.internal.pageSize.getWidth();
  const centerX = pageW / 2;
  const { lines, lineH, logoGap, logoMaxH, padTop, bandH, narrow } = layout;
  const footerTop = pageHeight - bandH;
  doc.setFillColor(33, 205, 192);
  doc.rect(0, footerTop, pageW, bandH, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(narrow ? 7 : 10);
  let yText = footerTop + padTop + lineH;
  lines.forEach((line) => {
    doc.text(line, centerX, yText, { align: "center" });
    yText += lineH;
  });
  // After loop yText points *past* the last baseline; logo must start after last line, not an extra lineH down.
  const logoY = yText - lineH + logoGap;
  try {
    const imgProps = doc.getImageProperties(hlthtekLogoSrc);
    const maxWidth = narrow ? 25 : 40;
    const ratio = imgProps.width / imgProps.height;
    let width = maxWidth;
    let height = width / ratio;
    if (height > logoMaxH) {
      height = logoMaxH;
      width = height * ratio;
    }
    doc.addImage(hlthtekLogoSrc, "PNG", centerX - width / 2, logoY, width, height);
  } catch (e) {
    doc.setFontSize(narrow ? 6 : 9);
    doc.text("Hlthtek", centerX, logoY + (narrow ? 1.5 : 2.5), { align: "center" });
  }
}

/** Layout constants for invoice PDF (A4 vs A5 portrait). */
function getInvoicePdfPageLayout(doc, paperSize) {
  const PAGE_WIDTH = doc.internal.pageSize.getWidth();
  const PAGE_HEIGHT = doc.internal.pageSize.getHeight();
  const narrow = paperSize === "a5";
  // A5 left labels at x≈6; values start after label width. A4 keeps original layout.
  const labelX = narrow ? 6 : 10;
  const valueX = narrow ? 32 : 28;
  // Right column must start after left block.
  const rightX = narrow ? 78 : 120;
  const valueOffset = narrow ? 13 : 16;
  const sideMargin = narrow ? 5 : 10;
  const addrWrap = narrow ? Math.max(28, rightX - valueX - 4) : 70;
  const centerWrap = narrow
    ? Math.max(20, PAGE_WIDTH - sideMargin - (rightX + valueOffset) - 2)
    : 65;
  // Header band height: A5 uses compact 13mm header vs A4's 20mm
  const headerBandH = narrow ? 13 : 20;
  // Vertical spacing between patient info lines
  const infoLineGap = narrow ? 2.5 : 3.5;
  // Y start after header
  const contentStartY = narrow ? 18 : 30;
  // Title-to-info gap
  const titleGap = narrow ? 4 : 8;
  return {
    PAGE_WIDTH,
    PAGE_HEIGHT,
    narrow,
    labelX,
    valueX,
    rightX,
    valueOffset,
    addrWrap,
    centerWrap,
    headerBandH,
    infoLineGap,
    contentStartY,
    titleGap,
    tableFont: narrow ? 4.5 : 7,
    tableHeadFont: narrow ? 5 : 8,
    paymentFont: narrow ? 5.5 : 9,
    headerTitleFont: narrow ? 8 : 15,
    sideMargin,
    /** Keep "amount in words" left of the totals column on narrow pages. */
    amountWordsRightEdge: narrow ? rightX - 4 : PAGE_WIDTH - sideMargin - 2,
    /** Table cell padding for A5 vs A4 */
    tableCellPadding: narrow
      ? { top: 1, bottom: 1, left: 1, right: 1 }
      : { top: 2, bottom: 2, left: 2, right: 2 },
    /** Header logo max dimensions */
    headerLogoMaxW: narrow ? 30 : 45,
    headerLogoMaxH: narrow ? 9 : 14,
    /** Info section font size */
    infoFontSize: narrow ? 5.5 : 7,
    /** Title font size */
    titleFontSize: narrow ? 7 : 10,
  };
}

/** jsPDF-AutoTable: line items — SAC + Qty centered; numeric cols right; description left (head + body). */
function alignInvoiceLineItemsCell(data) {
  if (data.section !== "head" && data.section !== "body") return;
  const i = data.column.index;
  if (i === 0) data.cell.styles.halign = "left";
  else if (i === 1 || i === 2) data.cell.styles.halign = "center";
  else data.cell.styles.halign = "right";
}

/**
 * "Received with thanks: " + amount in words. A4: 10pt, usual wrap.
 * A5: step font down so the amount stays on the same line as the prefix when it fits.
 */
function getThanksInWordsLayout(doc, receivedText, amountText, thanksRightEdge, narrow) {
  if (!narrow) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const textWidth = doc.getTextWidth(receivedText);
    doc.setFont("helvetica", "bold");
    const maxAmountWidth = Math.max(26, thanksRightEdge - (10 + textWidth));
    const wrappedAmount = doc.splitTextToSize(amountText, maxAmountWidth);
    return {
      thanksFs: 10,
      textWidth,
      wrappedAmount,
      lineHeight: 5,
    };
  }
  let best = {
    thanksFs: 5,
    textWidth: 0,
    wrappedAmount: [amountText],
    lineHeight: 4,
  };
  for (let fs = 10; fs >= 5; fs -= 0.5) {
    doc.setFontSize(fs);
    doc.setFont("helvetica", "normal");
    const tw = doc.getTextWidth(receivedText);
    doc.setFont("helvetica", "bold");
    const maxW = Math.max(14, thanksRightEdge - (10 + tw));
    const wrapped = doc.splitTextToSize(amountText, maxW);
    const lh = fs <= 6 ? 3.8 : fs <= 7.5 ? 4.2 : fs <= 9 ? 4.6 : 5;
    best = {
      thanksFs: fs,
      textWidth: tw,
      wrappedAmount: wrapped,
      lineHeight: lh,
    };
    if (wrapped.length <= 1) {
      break;
    }
  }
  return best;
}

const ViewInvoice = ({ mode = "invoice" }) => {
  const location = useLocation();
  const history = useHistory();
  const brandingData = useContext(BrandingContext);

  //only props / route state
  const { data, serviceMaster = [], patientMaster = [] } = location.state || {};
  const [itemState, setItemState] = useState([]);
  const [fetchedPatient, setFetchedPatient] = useState(null);
  const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState(null);
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);
  /** PDF / print page format: A4 (default) or A5. */
  const [invoicePaperSize, setInvoicePaperSize] = useState("a4");
  const { userInfo, loading: permissionLoading } = usePermissions();
  const REACT_APP_BACKEND_ROUTE_URL =
    process.env.REACT_APP_BACKEND_ROUTE_URL || "";
  const authToken = sessionStorage.getItem("accessToken") ?? "";
  //  const userRole = userInfo?.role_permission?.[0]?.role || "";
  // const userRole = "billingadmin";
  // const isFacilityAdmin = userRole === "billingadmin";
  const userRole = userInfo?.role_permission?.[0]?.role || "";
  const isBillingAdmin = userRole?.toLowerCase() === "billingadmin";
  const isFacilityAdmin = userRole === "facility user";

  /* =========================================================
      HELPERS
  ========================================================= */
  const safeNumber = (val) => {
    const n = Number(val);
    return isNaN(n) ? 0 : n;
  };

  const buildFacilityAddress = (info) => {
    const parts = [
      info?.facility_address_line_1,
      info?.facility_address_line_2,
      info?.facility_address_line_3,
      info?.city,
      info?.state,
      info?.pincode ? `PIN: ${info.pincode}` : null,
    ];
    return parts.filter((x) => x && String(x).trim() !== "").join(", ");
  };

  const formatMoney = (val) => safeNumber(val).toFixed(2);
  const formatReferredBy = (value) => {
    const normalized = value == null ? "" : String(value).trim();
    return normalized || "N/A";
  };

  const formatDate = (raw) => {
    if (!raw || raw === "N/A") return "N/A";
    try {
      const d = raw instanceof Date ? raw : new Date(raw);
      if (isNaN(d.getTime())) return String(raw);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
      return String(raw);
    }
  };

  const formatAgeGenderLine = (p, d) => {
    const age = p?.age ?? p?.age_years ?? d?.age;
    const genderRaw = p?.gender ?? p?.sex ?? d?.gender ?? "N/A";
    const genderStr =
      genderRaw != null && String(genderRaw).trim() !== ""
        ? String(genderRaw).trim()
        : "N/A";
    const ageStr =
      age != null &&
        String(age).trim() !== "" &&
        String(age).toLowerCase() !== "n/a"
        ? `${age} Years`
        : "N/A";
    return `${ageStr} / ${genderStr}`;
  };

  const formatContactNumbers = (p, d) => {
    const primary =
      p?.phone_no ||
      p?.phone ||
      p?.contact_no ||
      p?.mobile_no ||
      d?.phone ||
      d?.phone_no ||
      "";
    const alternateRaw =
      p?.alternate_phone ??
      p?.alternate_phone_no ??
      p?.alternate_contact_no ??
      p?.secondary_phone ??
      p?.emergency_phone ??
      p?.emergency_contact ??
      p?.phone_no_2 ??
      p?.mobile_no_2 ??
      p?.alternate_mobile ??
      d?.alternate_phone ??
      d?.alternate_phone_no ??
      "";
    const alt = alternateRaw != null ? String(alternateRaw).trim() : "";
    const pri = primary != null ? String(primary).trim() : "";
    if (!pri && !alt) return "N/A";
    if (pri && alt && alt !== pri) return `${pri} / ${alt}`;
    return pri || alt;
  };

  const getInvoiceDate = (d) => {
    return (
      d?.invoiceDate ||
      d?.invoice_date ||
      d?.invoice_date_time ||
      d?.date ||
      d?.created_at ||
      d?.createdAt ||
      d?.due_date ||
      d?.dueDate ||
      null
    );
  };

  const getFinalTotal = (d) => {
    return safeNumber(
      d?.total ||
      d?.total_amount ||
      d?.totalAmount ||
      d?.payable_amount ||
      d?.amount ||
      d?.subtotal ||
      0,
    );
  };

  /** Bill = sum of line gross; discount = line amounts + invoice-level; patient payable = bill − discount. */
  const getInvoiceSummaryFromItems = (normalizedItems, invoiceData) => {
    const sumGross = normalizedItems.reduce((s, i) => s + safeNumber(i.gross), 0);
    const sumLineDiscount = normalizedItems.reduce(
      (s, i) => s + safeNumber(i.discount_amount),
      0,
    );
    const invoiceDiscPct = safeNumber(invoiceData?.discount ?? 0);
    const invoiceDiscAmtFromApi = safeNumber(
      invoiceData?.discount_amount ??
      invoiceData?.total_discount ??
      invoiceData?.invoice_discount_amount ??
      0,
    );
    let invoiceLevelDiscount = invoiceDiscAmtFromApi;
    if (invoiceLevelDiscount === 0 && invoiceDiscPct > 0 && sumLineDiscount === 0) {
      invoiceLevelDiscount = (sumGross * invoiceDiscPct) / 100;
    }
    const totalDiscountAmount = sumLineDiscount + invoiceLevelDiscount;
    const billAmount = sumGross;
    const patientPayable = billAmount - totalDiscountAmount;
    return {
      billAmount,
      totalDiscountAmount,
      patientPayable,
      sumLineDiscount,
      invoiceLevelDiscount,
    };
  };

  /**
   * When discount is only at invoice level (no per-line amounts from API), split it by gross
   * so Discount % and Discount (₹) columns match the summary totals.
   */
  const mergeDisplayDiscounts = (normalizedItems, summary) => {
    if (!normalizedItems?.length) return normalizedItems;
    const sumGross = normalizedItems.reduce((s, i) => s + safeNumber(i.gross), 0);
    if (sumGross <= 0) return normalizedItems.map((i) => ({ ...i }));

    const sumLineDiscount = normalizedItems.reduce(
      (s, i) => s + safeNumber(i.discount_amount),
      0,
    );
    const totalDiscount = safeNumber(summary?.totalDiscountAmount);
    const toAllocate = Math.max(0, totalDiscount - sumLineDiscount);
    if (toAllocate <= 0.0001) return normalizedItems.map((i) => ({ ...i }));

    const n = normalizedItems.length;
    let allocatedSoFar = 0;
    return normalizedItems.map((item, idx) => {
      const gross = safeNumber(item.gross);
      const lineDisc = safeNumber(item.discount_amount);
      let extra = 0;
      if (idx === n - 1) {
        extra = Math.max(0, toAllocate - allocatedSoFar);
      } else {
        extra = (toAllocate * gross) / sumGross;
        allocatedSoFar += extra;
      }
      const da = lineDisc + extra;
      const dp = gross > 0 ? (da / gross) * 100 : 0;
      return { ...item, discount_amount: da, discount_percent: dp };
    });
  };

  /* =========================================================
      SERVICE MASTER LOOKUP
  ========================================================= */
  const getServiceNameById = (id) => {
    if (!id) return "--";
    const found = serviceMaster?.find(
      (s) => s?.medical_service_id === id || s?.id === id,
    );
    return found?.name || found?.item_name || "--";
  };

  /* =========================================================
      PATIENT MASTER LOOKUP
  ========================================================= */
  const patientId = data?.patient_id || data?.patientId;
  const patient = useMemo(() => {
    if (!patientId) return null;
    return patientMaster?.find((p) => p?.patient_id === patientId) || null;
  }, [patientId, patientMaster]);

  // When coming from invoice list we don't have patientMaster; fetch patient by id
  useEffect(() => {
    if (!data || !patientId || patient || !authToken || !REACT_APP_BACKEND_ROUTE_URL)
      return;
    let cancelled = false;
    const fetchPatient = async () => {
      try {
        const res = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}get_filtered_patient_data/`,
          {
            filter_type: "patient_id",
            filter_details: { patient_id: patientId },
            multipleResults: true,
          },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (cancelled) return;
        if (res?.data?.status === "Success" && Array.isArray(res?.data?.patient_details)) {
          const first = res.data.patient_details[0];
          if (first) setFetchedPatient(first);
        }
      } catch (e) {
        if (!cancelled) console.error("ViewInvoice: fetch patient failed", e);
      }
    };
    fetchPatient();
    return () => { cancelled = true; };
  }, [data, patientId, patient, authToken, REACT_APP_BACKEND_ROUTE_URL]);

  const displayPatient = patient || fetchedPatient;

  /** Clinic / facility name for signatory line (same sources as Center in the invoice). */
  const getClinicNameForSignatory = () => {
    const raw =
      data?.center ||
      data?.facility_name ||
      userInfo?.facility_name ||
      sessionStorage.getItem("facility_display_name") ||
      displayPatient?.facility_name ||
      "";
    return String(raw).trim();
  };

  /**
   * Authorised signatory second line: "ClinicName (UserName)" for invoice & receipt PDF + screen.
   */
  const getSignatoryLine = () => {
    const clinic = getClinicNameForSignatory();
    const person = (userInfo?.name || "").trim();
    if (clinic && person) return `${clinic} (${person})`;
    if (person) return person;
    return clinic || "N/A";
  };

  const normalizeItem = (item = {}) => {
    // const department =
    //   item?.department ||
    //   item?.department_name ||
    //   item?.dept ||
    //   item?.departmentName ||
    //   "N/A";

    // in itemsList service id stored in `items`
    const serviceId = item?.items || item?.item_id || item?.service_id;
    const item_name =
      item?.item_name ||
      item?.itemName ||
      item?.service_name ||
      item?.name ||
      item?.description ||
      getServiceNameById(serviceId) ||
      "--";

    const quantity = safeNumber(item?.quantity ?? item?.qty ?? 0);
    const item_price = safeNumber(
      item?.item_price ?? item?.price ?? item?.rate ?? 0,
    );

    const gross = safeNumber(item?.gross ?? item_price * quantity);

    const pctRaw =
      item?.discount_percentage ??
      item?.discount_percent ??
      item?.discount_pct ??
      null;

    let discount_amount = safeNumber(
      item?.discount_amount ?? item?.line_discount ?? item?.discount_value ?? 0,
    );
    if (discount_amount === 0 && pctRaw != null && safeNumber(pctRaw) > 0) {
      discount_amount = gross * (safeNumber(pctRaw) / 100);
    }
    if (discount_amount === 0 && item?.discount != null && item?.discount !== "") {
      discount_amount = safeNumber(item.discount);
    }

    let discount_percent = safeNumber(pctRaw);
    if (discount_percent === 0 && gross > 0 && discount_amount > 0) {
      discount_percent = (discount_amount / gross) * 100;
    }

    const tax = safeNumber(item?.tax ?? item?.tax_amount ?? 0);

    const subtotal = safeNumber(item?.subtotal ?? item?.amount ?? gross);

    return {
      // department,
      item_name,
      serviceId,
      quantity,
      item_price,
      gross,
      discount_amount,
      discount_percent,
      tax,
      subtotal,
    };
  };

  /* =========================================================
      LOAD ITEMS
  ========================================================= */
  useEffect(() => {
    if (!sessionStorage.getItem("accessToken")) {
      history.push("/login");
      return;
    }

    if (!data) {
      toast.error("Invoice data not found. Redirecting...");
      history.push("/doctor/invoice");
      return;
    }

    const items = pickInvoiceLineItems(data);
    const mergedItems = enrichLineItemsWithServiceNames(
      items,
      buildServiceNameByIdMap(serviceMaster),
    );

    setItemState(Array.isArray(mergedItems) ? mergedItems : []);
  }, [data, history, serviceMaster]);

  /* =========================================================
      BRANDING
  ========================================================= */
  const collateralImageItem = brandingData?.find(
    (item) => item.name === "collateral_image",
  );
  const collateralImage = collateralImageItem?.content || null;

  /* =========================================================
      PDF HELPERS
  ========================================================= */
  const numberToWords = (num) => {
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100)
        return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
      if (n < 1000)
        return (
          a[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " " + inWords(n % 100) : "")
        );
      if (n < 100000)
        return (
          inWords(Math.floor(n / 1000)) +
          " Thousand" +
          (n % 1000 ? " " + inWords(n % 1000) : "")
        );
      // Indian numbering system: Lakh (1,00,000)
      if (n < 10000000)
        return (
          inWords(Math.floor(n / 100000)) +
          " Lakh" +
          (n % 100000 ? " " + inWords(n % 100000) : "")
        );
      // Indian numbering system: Crore (1,00,00,000)
      if (n < 1000000000)
        return (
          inWords(Math.floor(n / 10000000)) +
          " Crore" +
          (n % 10000000 ? " " + inWords(n % 10000000) : "")
        );
      return "";
    };

    return inWords(num);
  };

  /* =========================================================
      DOWNLOAD PDF
  ========================================================= */
  const handleDownload = (withLetterhead = true) => {
    if (!data) return;

    const normalizedItems = itemState.map((it) => normalizeItem(it));
    if (!normalizedItems.length) {
      toast.error("No items found!");
      return;
    }

    const summaryPdf = getInvoiceSummaryFromItems(normalizedItems, data);
    const displayItemsPdf = mergeDisplayDiscounts(normalizedItems, summaryPdf);
    const billAmtPdf =
      summaryPdf.billAmount > 0 ? summaryPdf.billAmount : safeNumber(getFinalTotal(data));
    const discAmtPdf =
      summaryPdf.billAmount > 0 ? summaryPdf.totalDiscountAmount : 0;
    const patientPayablePdf =
      summaryPdf.billAmount > 0 ? summaryPdf.patientPayable : safeNumber(getFinalTotal(data));

    const doc = new jsPDF("p", "mm", getInvoicePdfJsFormat(invoicePaperSize));
    const {
      PAGE_WIDTH,
      labelX,
      valueX,
      rightX,
      valueOffset,
      addrWrap,
      centerWrap,
      tableFont,
      tableHeadFont,
      paymentFont,
      headerTitleFont,
      sideMargin,
      narrow,
      amountWordsRightEdge,
      headerBandH,
      infoLineGap,
      contentStartY,
      titleGap,
      tableCellPadding,
      headerLogoMaxW,
      headerLogoMaxH,
      infoFontSize,
      titleFontSize,
    } = getInvoicePdfPageLayout(doc, invoicePaperSize);

    const pdfPaymentsList = Array.isArray(data?.payment_details)
      ? data.payment_details
      : Array.isArray(data?.payments)
        ? data.payments
        : data?.payment
          ? [data.payment]
          : [];
    const pdfPayment = pdfPaymentsList[0] || data?.payment || data?.payments?.[0] || {};
    const payment_date = data?.payment_date || pdfPayment?.created_at || "N/A";
    const receipt_no = data?.receipt_no || pdfPayment?.receipt_number || "N/A";
    const payment_mode = data?.payment_mode || pdfPayment?.mode_of_payment || "N/A";
    const ref_no = data?.ref_no || pdfPayment?.transaction_ref_no || "N/A";
    let amount_paid = 0;
    if (pdfPaymentsList.length > 0) {
      amount_paid = pdfPaymentsList.reduce(
        (sum, p) => sum + safeNumber(p?.payment_amount ?? 0),
        0
      );
    } else {
      amount_paid = safeNumber(data?.amount_paid ?? data?.partial_payment ?? pdfPayment?.payment_amount ?? 0);
    }
    const matchedReceiptPayment =
      pdfPaymentsList.find((p) => (p?.receipt_number ?? "") === receipt_no) ||
      pdfPaymentsList[pdfPaymentsList.length - 1] ||
      pdfPayment;
    const receipt_amount_paid = safeNumber(
      matchedReceiptPayment?.payment_amount ??
      data?.amount_paid ??
      data?.partial_payment ??
      0,
    );

    const pdfDueAmount = safeNumber(patientPayablePdf - amount_paid);
    const pdfStatusLabel = pdfDueAmount > 0.01 ? "DUE" : "PAID";

    // ✅ Header logo (top right): collateral_image from branding, else fallback
    const headerLogoDataUrl = collateralImage
      ? `data:image/png;base64,${collateralImage}`
      : null;

    const invoiceFooterLayout = measureInvoicePdfFooter(doc, invoicePaperSize);

    // ===== HEADER (same output, global header for every page) =====
    // Important: function declaration use kiya because const hoisting problem hota hai
    function drawHeader() {
      doc.setFillColor(33, 205, 192);
      doc.rect(0, 0, PAGE_WIDTH, headerBandH, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(headerTitleFont);
      doc.text(
        sessionStorage.getItem("facility_display_name") ||
        data?.facility_name ||
        displayPatient?.facility_name ||
        "Super Galaxy Health",
        narrow ? 6 : 10,
        narrow ? 8 : 12,
      );

      doc.setTextColor(0);
      try {
        if (headerLogoDataUrl) {
          const imgProps = doc.getImageProperties(headerLogoDataUrl);
          const ratio = imgProps.width / imgProps.height;
          let width = headerLogoMaxW;
          let height = width / ratio;
          if (height > headerLogoMaxH) {
            height = headerLogoMaxH;
            width = height * ratio;
          }
          doc.addImage(
            headerLogoDataUrl,
            "PNG",
            PAGE_WIDTH - sideMargin - width,
            narrow ? 2 : 3,
            width,
            height
          );
        }
      } catch (e) {
        // fallback - ignore if logo not found
      }
    }

    function drawFooterTemplate() {
      // A5: no footer — skip entirely
      if (narrow) return;
      drawInvoicePdfFooter(doc, HlthtekFooterLogo, invoiceFooterLayout);
    }

    // ===== GLOBAL HOOK - HAR PAGE PE HEADER + FOOTER (letterhead on) / blank bands (letterhead off) =====
    // A5: no footer needed, so bottom margin is minimal
    const bottomMargin = narrow
      ? 8
      : withLetterhead
        ? Math.max(25, invoiceFooterLayout.bandH + 8)
        : Math.max(22, invoiceFooterLayout.bandH + 5);
    doc.autoTableSetDefaults({
      margin: {
        bottom: bottomMargin,
        ...(withLetterhead ? {} : { top: narrow ? 20 : 30 }),
      },
      didDrawPage: withLetterhead
        ? function () {
          drawHeader();
          drawFooterTemplate();
        }
        : function () { },
    });

    const PAGE_HEIGHT = doc.internal.pageSize.height;
    let y = 15;

    if (withLetterhead) {
      drawHeader();
    }
    y = contentStartY;

    // TITLE & STATUS
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const titleText = mode === "receipt" ? "Bill Cum Receipt" : "Bill of Supply";
    doc.text(titleText, labelX, y);

    const statusStr = pdfStatusLabel;
    doc.setFontSize(narrow ? 6.5 : 9);
    if (statusStr === "PAID") {
      doc.setTextColor(6, 95, 70); // Green
    } else {
      doc.setTextColor(153, 27, 27); // Red (DUE)
    }
    doc.text(`Status: ${statusStr}`, labelX + doc.getTextWidth(titleText) + (narrow ? 3 : 5), y);
    doc.setTextColor(0); // Reset to black
    y += titleGap;

    doc.setFontSize(infoFontSize);
    doc.setFont("helvetica", "normal");

    const patientY = y;

    doc.setFont("helvetica", "bold");
    doc.text("Name:", labelX, patientY);
    doc.setFont("helvetica", "normal");
    doc.text(
      data?.patient_name || data?.patientName || displayPatient?.name || "N/A",
      valueX,
      patientY,
    );

    doc.setFont("helvetica", "bold");
    doc.text("Age/Gender:", labelX, patientY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(formatAgeGenderLine(displayPatient, data), valueX, patientY + infoLineGap);

    doc.setFont("helvetica", "bold");
    doc.text("Contact No:", labelX, patientY + infoLineGap * 2);
    doc.setFont("helvetica", "normal");
    doc.text(formatContactNumbers(displayPatient, data), valueX, patientY + infoLineGap * 2);

    doc.setFont("helvetica", "bold");
    doc.text("Address:", labelX, patientY + infoLineGap * 3);
    doc.setFont("helvetica", "normal");

    const patientAddress = data?.address ||
      displayPatient?.address ||
      [
        displayPatient?.address_line,
        displayPatient?.locality,
        displayPatient?.city || displayPatient?.district,
        displayPatient?.state,
        displayPatient?.pincode
      ].filter(v => v && !["null", "undefined", "n/a"].includes(v.toString().toLowerCase())).join(", ");

    const finalAddress = patientAddress || "N/A";
    const addressLines = doc.splitTextToSize(finalAddress, addrWrap).slice(0, 2);
    for (let i = 0; i < addressLines.length; i++) {
      doc.text(addressLines[i], valueX, patientY + infoLineGap * 3 + i * infoLineGap);
    }

    const referredY = patientY + infoLineGap * 3 + addressLines.length * infoLineGap;
    doc.setFont("helvetica", "bold");
    doc.text("Referred by:", labelX, referredY);
    doc.setFont("helvetica", "normal");
    doc.text(
      formatReferredBy(data?.referred_by || displayPatient?.referred_by),
      valueX,
      referredY,
    );

    const patIdValue =
      displayPatient?.patient_id ||
      data?.patient_id ||
      data?.patientId ||
      "N/A";

    // RIGHT BLOCK (rightX from page layout for A4 vs A5)
    const rightY = patientY;

    doc.setFont("helvetica", "bold");
    doc.text(mode === "receipt" ? "Receipt No:" : "Bill No:", rightX, rightY);
    doc.setFont("helvetica", "normal");
    doc.text(mode === "receipt" ? receipt_no : (data?.invoice_number || "N/A"), rightX + valueOffset, rightY);

    doc.setFont("helvetica", "bold");
    doc.text("Pat ID:", rightX, rightY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(patIdValue, rightX + valueOffset, rightY + infoLineGap);

    if (mode === "invoice" && pdfPaymentsList.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Date:", rightX, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(getInvoiceDate(data)) || "N/A", rightX + valueOffset, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "bold");
      doc.text("Center:", rightX, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "normal");
      const cName = data?.center || data?.facility_name || userInfo?.facility_name || sessionStorage.getItem("facility_display_name") || "N/A";
      const wrappedCName = doc.splitTextToSize(cName, centerWrap).slice(0, 1);
      doc.text(wrappedCName, rightX + valueOffset, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "bold");
      doc.text("Address:", rightX, rightY + infoLineGap * 4);
      doc.setFont("helvetica", "normal");
      const cAddr = data?.center_address || data?.facility_address || buildFacilityAddress(userInfo) || "N/A";
      const wrappedCAddr = doc.splitTextToSize(cAddr, centerWrap).slice(0, 2);
      doc.text(wrappedCAddr, rightX + valueOffset, rightY + infoLineGap * 4);
      y = rightY + infoLineGap * 6;
    } else {
      doc.setFont("helvetica", "bold");
      doc.text("Date:", rightX, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "normal");
      doc.text(
        formatDate(getInvoiceDate(data)) || "N/A",
        rightX + valueOffset,
        rightY + infoLineGap * 2,
      );

      doc.setFont("helvetica", "bold");
      doc.text("Center:", rightX, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "normal");
      const centerName = data?.center || data?.facility_name || userInfo?.facility_name || sessionStorage.getItem("facility_display_name") || "N/A";
      const wrappedCenterName = doc.splitTextToSize(centerName, centerWrap).slice(0, 1);
      doc.text(wrappedCenterName, rightX + valueOffset, rightY + infoLineGap * 3);

      doc.setFont("helvetica", "bold");
      doc.text("Address:", rightX, rightY + infoLineGap * 4);
      doc.setFont("helvetica", "normal");
      const centerAddrArr = data?.center_address || data?.facility_address || buildFacilityAddress(userInfo) || "N/A";
      const wrappedCenterAddr = doc.splitTextToSize(centerAddrArr, centerWrap).slice(0, 2);
      doc.text(wrappedCenterAddr, rightX + valueOffset, rightY + infoLineGap * 4);
      y = rightY + infoLineGap * 5;
    }

    // Prescribed doctor (comment only - future use)
    // doc.setFont("helvetica", "bold");
    // doc.text("Prescribed Doctor:", rightX, rightY + 10.5);
    // doc.setFont("helvetica", "normal");
    // doc.text("--", rightX + 28, rightY + 10.5);

    // Auth Code (comment only - future use)
    // doc.setFont("helvetica", "bold");
    // doc.text("Emp No/Auth Code:", rightX, rightY + 14);
    // doc.setFont("helvetica", "normal");
    // doc.text("--", rightX + 28, rightY + 14);

    y = Math.max(rightY + (narrow ? 16 : 23), referredY + 1);

    // ✅ PDF TABLE
    const tableBody = displayItemsPdf.map((item) => [
      // item.department || "N/A",
      item.item_name || "--",

      // SAC Code (comment only - future use)
      // item.sac_code || "--",
      "--",

      item.quantity || 0,
      Number(item.item_price || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.gross || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.discount_percent || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.discount_amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.tax || data?.tax || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.subtotal || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
    ]);

    doc.autoTable({
      startY: y,
      head: [
        [
          // "Department",
          "Service Description",
          "SAC Code",
          "Qty",
          "Price (Rs.)",
          "Gross",
          "Discount %",
          "Discount (Rs.)",
          "Tax",
          "Amount (Rs.)",
        ],
      ],
      body: tableBody,
      theme: "plain",
      styles: {
        font: "helvetica",
        fontSize: tableFont,
        cellPadding: narrow
          ? tableCellPadding
          : { top: 2, bottom: 2, left: 1, right: 1 },
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
      },
      didParseCell: alignInvoiceLineItemsCell,
      headStyles: {
        fillColor: [199, 244, 239],
        textColor: 0,
        fontStyle: "bold",
        fontSize: tableHeadFont,
      },
      margin: { left: sideMargin, right: sideMargin + 2 },
      pageBreak: "auto",
    });


    const paymentTableBody =
      pdfPaymentsList.length > 0
        ? pdfPaymentsList.map((p) => [
          formatDate(p?.created_at || "N/A"),
          p?.receipt_number ?? "N/A",
          p?.mode_of_payment ?? "N/A",
          p?.transaction_ref_no ?? "N/A",
          Number(safeNumber(p?.payment_amount ?? 0)).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          }),
        ])
        : [
          [
            formatDate(payment_date),
            receipt_no,
            payment_mode,
            ref_no,
            Number(amount_paid || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
            }),
          ],
        ];

    let currentY = doc.lastAutoTable.finalY + 3;
    if (currentY + 60 > PAGE_HEIGHT) {
      doc.addPage();
      currentY = withLetterhead ? 20 : 30;
    }

    if (mode === "invoice") {
      doc.autoTable({
        startY: currentY,
        head: [["Payment Date", "Receipt No", "Mode", "Ref No", "Amount"]],
        body: paymentTableBody,
        theme: "plain",
        styles: {
          font: "helvetica",
          fontSize: paymentFont,
          halign: "left",
          cellPadding: { top: 1.5, bottom: 1.5, left: 0, right: 2 },
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "left" },
          2: { halign: "left" },
          3: { halign: "left" },
          4: { halign: "right" },
        },
        margin: { left: sideMargin, right: sideMargin + 2 },
      });
      currentY = doc.lastAutoTable.finalY + 3;
    } else {
      // For Receipt, show only Mode, Ref No, Amount
      const receiptTableBody = pdfPaymentsList.length > 0
        ? pdfPaymentsList.map((p) => [
          p?.mode_of_payment ?? data?.payment_mode ?? "N/A",
          p?.transaction_ref_no ?? data?.ref_no ?? "N/A",
          Number(safeNumber(p?.payment_amount ?? 0)).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          }),
        ])
        : [
          [
            payment_mode,
            ref_no,
            Number(amount_paid || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
            }),
          ],
        ];

      doc.autoTable({
        startY: currentY,
        head: [["Mode", "Ref No", "Amount"]],
        body: receiptTableBody,
        theme: "plain",
        tableWidth: "wrap",
        styles: {
          font: "helvetica",
          fontSize: paymentFont,
          halign: "left",
          cellPadding: { top: 1.2, bottom: 1.2, left: 0, right: 3 },
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "left" },
          2: { halign: "right" },
        },
        margin: { left: sideMargin, right: sideMargin + 2 },
      });
      currentY = doc.lastAutoTable.finalY + 3;
    }

    // const PAGE_WIDTH = doc.internal.pageSize.width;
    const RIGHT_MARGIN = sideMargin + 2;
    const RIGHT_X = PAGE_WIDTH - RIGHT_MARGIN;

    const summaryTotalsFontSize = narrow ? Math.max(5, 8 - 3) : 8;
    const summaryLineGap = narrow ? 2.8 : 5;
    const sumY = (row) =>
      narrow
        ? currentY + summaryLineGap * row
        : currentY + [0, 5, 10, 16, 21][row];
    doc.setFont("helvetica", "bold");
    doc.setFontSize(summaryTotalsFontSize);

    if (mode === "invoice") {
      doc.text(
        `Bill Amount: Rs. ${Number(billAmtPdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(0),
        { align: "right" }
      );

      doc.text(
        `Discount Amount: Rs. ${Number(discAmtPdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(1),
        { align: "right" }
      );

      doc.text(
        `Patient Payable: Rs. ${Number(patientPayablePdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(2),
        { align: "right" }
      );

      doc.setTextColor(14, 63, 102);
      doc.text(
        `Amount Paid: Rs. ${Number(mode === "receipt" ? receipt_amount_paid : amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(3),
        { align: "right" }
      );

      doc.setTextColor(0);
      doc.text(
        `Due Amount: Rs. ${Number(patientPayablePdf - amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(4),
        { align: "right" }
      );
    } else {
      doc.text(
        `Bill Amount: Rs. ${Number(billAmtPdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(0),
        { align: "right" }
      );
      doc.text(
        `Discount Amount: Rs. ${Number(discAmtPdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(1),
        { align: "right" }
      );
      doc.text(
        `Patient Payable: Rs. ${Number(patientPayablePdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(2),
        { align: "right" }
      );

      doc.setTextColor(14, 63, 102);
      doc.text(
        `Amount Paid: Rs. ${Number(mode === "receipt" ? receipt_amount_paid : amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(3),
        { align: "right" }
      );

      doc.setTextColor(0);
      doc.text(
        `Due Amount: Rs. ${Number(patientPayablePdf - amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(4),
        { align: "right" }
      );
    }
    doc.setTextColor(0);

    const amountForWordsPdf =
      mode === "receipt"
        ? Math.floor(Number(receipt_amount_paid || 0))
        : Math.floor(Number(patientPayablePdf || 0));
    const amountText = `${numberToWords(amountForWordsPdf)} Rupees Only`;

    const receivedText = "Received with thanks: ";
    const thanksRightEdge =
      narrow
        ? Math.min(amountWordsRightEdge + 6, RIGHT_X - 4)
        : RIGHT_X;
    const {
      thanksFs,
      textWidth,
      wrappedAmount,
      lineHeight,
    } = getThanksInWordsLayout(
      doc,
      receivedText,
      amountText,
      thanksRightEdge,
      narrow,
    );
    const thanksBlockHeight = 6 + wrappedAmount.length * lineHeight;

    const summaryEndY = narrow ? sumY(4) : currentY + 21;
    const gapAfterSummary = narrow ? 5 : 7;
    let thanksY = summaryEndY + gapAfterSummary;
    const footerReserve = narrow ? 6 : 22;
    if (thanksY + thanksBlockHeight + footerReserve > PAGE_HEIGHT) {
      doc.addPage();
      thanksY = withLetterhead ? (narrow ? 16 : 20) : (narrow ? 16 : 30);
    }

    doc.setFontSize(thanksFs);
    doc.setFont("helvetica", "normal");
    doc.text(receivedText, labelX, thanksY);
    doc.setFont("helvetica", "bold");
    doc.text(wrappedAmount, labelX + textWidth, thanksY);
    const lastLineY = thanksY + (wrappedAmount.length - 1) * lineHeight;

    const noteY = lastLineY + (narrow ? 5.5 : 9);
    const signatureY = narrow ? thanksY : noteY;

    doc.setFontSize(narrow ? 5.5 : 8);
    doc.setFont("helvetica", "normal");
    doc.text(`Note: This is a digitally generated ${mode === "receipt" ? "receipt" : "invoice"}.`, labelX, noteY);

    const signatoryFontSize = narrow ? Math.max(5, 8 - 2) : 8;
    const signatoryLineStep = narrow ? 6.75 : 9;
    doc.setFontSize(signatoryFontSize);
    doc.setFont("helvetica", "bold");
    doc.text("Authorised Signatory", RIGHT_X, signatureY, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.text(getSignatoryLine(), RIGHT_X, signatureY + signatoryLineStep, { align: "right" });


    doc.save(`${mode === "receipt" ? "Bill_Cum_Receipt" : "Invoice"}-${data?.invoice_number || "INV"}.pdf`);
  };

  const handlePrint = (withLetterhead = true) => {
    if (!data) return;

    const normalizedItems = itemState.map((it) => normalizeItem(it));
    if (!normalizedItems.length) {
      toast.error("No items found!");
      return;
    }

    const summaryPrint = getInvoiceSummaryFromItems(normalizedItems, data);
    const displayItemsPrint = mergeDisplayDiscounts(normalizedItems, summaryPrint);
    const billAmtPrint =
      summaryPrint.billAmount > 0 ? summaryPrint.billAmount : safeNumber(getFinalTotal(data));
    const discAmtPrint =
      summaryPrint.billAmount > 0 ? summaryPrint.totalDiscountAmount : 0;
    const patientPayablePrint =
      summaryPrint.billAmount > 0 ? summaryPrint.patientPayable : safeNumber(getFinalTotal(data));

    const doc = new jsPDF("p", "mm", getInvoicePdfJsFormat(invoicePaperSize));
    const {
      PAGE_WIDTH,
      labelX,
      valueX,
      rightX,
      valueOffset,
      addrWrap,
      centerWrap,
      tableFont,
      tableHeadFont,
      paymentFont,
      headerTitleFont,
      sideMargin,
      narrow,
      amountWordsRightEdge,
      headerBandH,
      infoLineGap,
      contentStartY,
      titleGap,
      tableCellPadding,
      headerLogoMaxW,
      headerLogoMaxH,
      infoFontSize,
      titleFontSize,
    } = getInvoicePdfPageLayout(doc, invoicePaperSize);

    const isA5Print = invoicePaperSize === "a5";
    // Keep a little extra side breathing room for A5 direct-print so browser/printer scaling is less likely.
    const printSideMargin = isA5Print ? sideMargin + 2 : sideMargin;
    const printRightMargin = printSideMargin + 2;

    const pdfPaymentsList = Array.isArray(data?.payment_details)
      ? data.payment_details
      : Array.isArray(data?.payments)
        ? data.payments
        : data?.payment
          ? [data.payment]
          : [];
    const pdfPayment = pdfPaymentsList[0] || data?.payment || data?.payments?.[0] || {};
    const payment_date = data?.payment_date || pdfPayment?.created_at || "N/A";
    const receipt_no = data?.receipt_no || pdfPayment?.receipt_number || "N/A";
    const payment_mode = data?.payment_mode || pdfPayment?.mode_of_payment || "N/A";
    const ref_no = data?.ref_no || pdfPayment?.transaction_ref_no || "N/A";
    let amount_paid = 0;
    if (pdfPaymentsList.length > 0) {
      amount_paid = pdfPaymentsList.reduce(
        (sum, p) => sum + safeNumber(p?.payment_amount ?? 0),
        0
      );
    } else {
      amount_paid = safeNumber(data?.amount_paid ?? data?.partial_payment ?? pdfPayment?.payment_amount ?? 0);
    }
    const matchedReceiptPayment =
      pdfPaymentsList.find((p) => (p?.receipt_number ?? "") === receipt_no) ||
      pdfPaymentsList[pdfPaymentsList.length - 1] ||
      pdfPayment;
    const receipt_amount_paid = safeNumber(
      matchedReceiptPayment?.payment_amount ??
      data?.amount_paid ??
      data?.partial_payment ??
      0,
    );

    const printDueAmount = safeNumber(patientPayablePrint - amount_paid);
    const printStatusLabel = printDueAmount > 0.01 ? "DUE" : "PAID";

    const headerLogoDataUrl = collateralImage ? `data:image/png;base64,${collateralImage}` : null;
    const printFooterLayout = measureInvoicePdfFooter(doc, invoicePaperSize);

    function drawHeader() {
      doc.setFillColor(33, 205, 192);
      doc.rect(0, 0, PAGE_WIDTH, headerBandH, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(headerTitleFont);
      doc.text(sessionStorage.getItem("facility_display_name") || data?.facility_name || displayPatient?.facility_name || "Super Galaxy Health", narrow ? 6 : 10, narrow ? 8 : 12);

      doc.setTextColor(0);
      try {
        if (headerLogoDataUrl) {
          const imgProps = doc.getImageProperties(headerLogoDataUrl);
          const ratio = imgProps.width / imgProps.height;
          let width = headerLogoMaxW;
          let height = width / ratio;
          if (height > headerLogoMaxH) {
            height = headerLogoMaxH;
            width = height * ratio;
          }
          doc.addImage(headerLogoDataUrl, "PNG", PAGE_WIDTH - printSideMargin - width, narrow ? 2 : 3, width, height);
        }
      } catch (e) { }
    }

    function drawFooterTemplate() {
      // A5: no footer — skip entirely
      if (narrow) return;
      drawInvoicePdfFooter(doc, HlthtekFooterLogo, printFooterLayout);
    }

    // A5: no footer, so bottom margin is minimal
    const bottomMarginPrint = narrow
      ? 8
      : withLetterhead
        ? Math.max(25, printFooterLayout.bandH + 8)
        : Math.max(22, printFooterLayout.bandH + 5);
    doc.autoTableSetDefaults({
      margin: {
        bottom: bottomMarginPrint,
        ...(withLetterhead ? {} : { top: narrow ? 20 : 30 }),
      },
      didDrawPage: withLetterhead
        ? function () {
          drawHeader();
          drawFooterTemplate();
        }
        : function () { },
    });

    const PAGE_HEIGHT = doc.internal.pageSize.height;
    let y = contentStartY;
    if (withLetterhead) {
      drawHeader();
    }

    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const titleText = mode === "receipt" ? "Bill Cum Receipt" : "Bill of Supply";
    doc.text(titleText, labelX, y);

    const statusStr = printStatusLabel;
    doc.setFontSize(narrow ? 6.5 : 9);
    if (statusStr === "PAID") {
      doc.setTextColor(6, 95, 70);
    } else {
      doc.setTextColor(153, 27, 27);
    }
    doc.text(`Status: ${statusStr}`, labelX + doc.getTextWidth(titleText) + (narrow ? 3 : 5), y);
    doc.setTextColor(0);
    y += titleGap;

    doc.setFontSize(infoFontSize);
    doc.setFont("helvetica", "normal");
    const patientY = y;
    doc.setFont("helvetica", "bold");
    doc.text("Name:", labelX, patientY);
    doc.setFont("helvetica", "normal");
    doc.text(data?.patient_name || data?.patientName || displayPatient?.name || "N/A", valueX, patientY);
    doc.setFont("helvetica", "bold");
    doc.text("Age/Gender:", labelX, patientY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(formatAgeGenderLine(displayPatient, data), valueX, patientY + infoLineGap);
    doc.setFont("helvetica", "bold");
    doc.text("Contact No:", labelX, patientY + infoLineGap * 2);
    doc.setFont("helvetica", "normal");
    doc.text(formatContactNumbers(displayPatient, data), valueX, patientY + infoLineGap * 2);
    doc.setFont("helvetica", "bold");
    doc.text("Address:", labelX, patientY + infoLineGap * 3);
    doc.setFont("helvetica", "normal");

    const printPatientAddress =
      data?.address ||
      displayPatient?.address ||
      [
        displayPatient?.address_line,
        displayPatient?.locality,
        displayPatient?.city || displayPatient?.district,
        displayPatient?.state,
        displayPatient?.pincode,
      ]
        .filter(
          (v) =>
            v && !["null", "undefined", "n/a"].includes(String(v).toLowerCase()),
        )
        .join(", ");

    const printPatientAddressFinal = printPatientAddress || "N/A";
    const addrLines = doc
      .splitTextToSize(printPatientAddressFinal, addrWrap)
      .slice(0, 2);
    for (let i = 0; i < addrLines.length; i++) {
      doc.text(addrLines[i], valueX, patientY + infoLineGap * 3 + i * infoLineGap);
    }

    const printReferredY = patientY + infoLineGap * 3 + addrLines.length * infoLineGap;
    doc.setFont("helvetica", "bold");
    doc.text("Referred by:", labelX, printReferredY);
    doc.setFont("helvetica", "normal");
    doc.text(
      formatReferredBy(data?.referred_by || displayPatient?.referred_by),
      valueX,
      printReferredY,
    );

    const printPatId =
      displayPatient?.patient_id ||
      data?.patient_id ||
      data?.patientId ||
      "N/A";

    const rightY = patientY;
    doc.setFont("helvetica", "bold");
    doc.text(mode === "receipt" ? "Receipt No:" : "Bill No:", rightX, rightY);
    doc.setFont("helvetica", "normal");
    doc.text(mode === "receipt" ? receipt_no : (data?.invoice_number || "N/A"), rightX + valueOffset, rightY);

    doc.setFont("helvetica", "bold");
    doc.text("Pat ID:", rightX, rightY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(printPatId, rightX + valueOffset, rightY + infoLineGap);

    const printCenterName =
      data?.center ||
      data?.facility_name ||
      userInfo?.facility_name ||
      sessionStorage.getItem("facility_display_name") ||
      "N/A";
    const printCenterAddress =
      data?.center_address ||
      data?.facility_address ||
      buildFacilityAddress(userInfo) ||
      "N/A";

    if (mode === "invoice" && pdfPaymentsList.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Date:", rightX, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(getInvoiceDate(data)) || "N/A", rightX + valueOffset, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "bold");
      doc.text("Center:", rightX, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "normal");
      const wrappedPrintCName = doc.splitTextToSize(printCenterName, centerWrap).slice(0, 1);
      doc.text(wrappedPrintCName, rightX + valueOffset, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "bold");
      doc.text("Address:", rightX, rightY + infoLineGap * 4);
      doc.setFont("helvetica", "normal");
      const wrappedCAddrStr = doc.splitTextToSize(printCenterAddress, centerWrap).slice(0, 2);
      doc.text(wrappedCAddrStr, rightX + valueOffset, rightY + infoLineGap * 4);
      y = rightY + infoLineGap * 6;
    } else {
      doc.setFont("helvetica", "bold");
      doc.text("Date:", rightX, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(getInvoiceDate(data)) || "N/A", rightX + valueOffset, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "bold");
      doc.text("Center:", rightX, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "normal");
      const wrappedCenterNamePrint = doc.splitTextToSize(printCenterName, centerWrap).slice(0, 1);
      doc.text(wrappedCenterNamePrint, rightX + valueOffset, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "bold");
      doc.text("Address:", rightX, rightY + infoLineGap * 4);
      doc.setFont("helvetica", "normal");
      const wrappedCAddr = doc.splitTextToSize(printCenterAddress, centerWrap).slice(0, 2);
      doc.text(wrappedCAddr, rightX + valueOffset, rightY + infoLineGap * 4);
      y = rightY + infoLineGap * 5;
    }

    y = Math.max(rightY + (narrow ? 16 : 23), printReferredY + 1);
    const tableBody = displayItemsPrint.map((item) => [
      // item.department || "N/A",
      item.item_name || "--",
      "--",
      item.quantity || 0,
      Number(item.item_price || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Number(item.gross || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Number(item.discount_percent || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Number(item.discount_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Number(item.tax || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Number(item.subtotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
    ]);

    doc.autoTable({
      startY: y,
      head: [[/* "Department", */ "Service Description", "SAC Code", "Qty", "Price (Rs.)", "Gross", "Discount %", "Discount (Rs.)", "Tax", "Amount (Rs.)"]],
      body: tableBody,
      theme: "plain",
      styles: {
        font: "helvetica",
        fontSize: tableFont,
        cellPadding: narrow
          ? tableCellPadding
          : { top: 2, bottom: 2, left: 1, right: 1 },
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
      },
      didParseCell: alignInvoiceLineItemsCell,
      headStyles: { fillColor: [199, 244, 239], textColor: 0, fontStyle: "bold", fontSize: tableHeadFont },
      margin: { left: printSideMargin, right: printRightMargin },
    });

    const paymentTableBody = pdfPaymentsList.length > 0
      ? pdfPaymentsList.map((p) => [formatDate(p?.created_at || "N/A"), p?.receipt_number ?? "N/A", p?.mode_of_payment ?? "N/A", p?.transaction_ref_no ?? "N/A", Number(safeNumber(p?.payment_amount ?? 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })])
      : [[formatDate(payment_date), receipt_no, payment_mode, ref_no, Number(amount_paid || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })]];

    let currentY = doc.lastAutoTable.finalY + 3;
    doc.autoTable({
      startY: currentY,
      head: [mode === "invoice" ? ["Payment Date", "Receipt No", "Mode", "Ref No", "Amount"] : ["Mode", "Ref No", "Amount"]],
      body: mode === "invoice" ? paymentTableBody : paymentTableBody.map(r => r.slice(2)),
      theme: "plain",
      tableWidth: mode === "receipt" ? "wrap" : "auto",
      styles: {
        font: "helvetica",
        fontSize: paymentFont,
        cellPadding: mode === "receipt"
          ? { top: 1.2, bottom: 1.2, left: 0, right: 3 }
          : { top: 1.5, bottom: 1.5, left: 0, right: 2 },
      },
      columnStyles: mode === "invoice"
        ? {
          0: { halign: "left" },
          1: { halign: "left" },
          2: { halign: "left" },
          3: { halign: "left" },
          4: { halign: "right" },
        }
        : {
          0: { halign: "left" },
          1: { halign: "left" },
          2: { halign: "right" },
        },
      margin: { left: printSideMargin, right: printRightMargin },
    });

    currentY = doc.lastAutoTable.finalY + 3;
    const RIGHT_X = PAGE_WIDTH - printRightMargin;
    const summaryTotalsFontSizePrint = narrow ? Math.max(5, 8 - 3) : 8;
    const summaryLineGapPrint = narrow ? 2.8 : 5;
    const sumYPrint = (row) =>
      narrow
        ? currentY + summaryLineGapPrint * row
        : currentY + [0, 5, 10, 16, 21][row];
    doc.setFont("helvetica", "bold");
    doc.setFontSize(summaryTotalsFontSizePrint);
    if (mode === "invoice") {
      doc.text(`Bill Amount: Rs. ${Number(billAmtPrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(0), { align: "right" });
      doc.text(`Discount Amount: Rs. ${Number(discAmtPrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(1), { align: "right" });
      doc.text(`Patient Payable: Rs. ${Number(patientPayablePrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(2), { align: "right" });
      doc.setTextColor(14, 63, 102);
      doc.text(`Amount Paid: Rs. ${Number(mode === "receipt" ? receipt_amount_paid : amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(3), { align: "right" });
      doc.setTextColor(0);
      doc.text(`Due Amount: Rs. ${Number(patientPayablePrint - amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(4), { align: "right" });
    } else {
      doc.text(`Bill Amount: Rs. ${Number(billAmtPrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(0), { align: "right" });
      doc.text(`Discount Amount: Rs. ${Number(discAmtPrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(1), { align: "right" });
      doc.text(`Patient Payable: Rs. ${Number(patientPayablePrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(2), { align: "right" });
      doc.setTextColor(14, 63, 102);
      doc.text(`Amount Paid: Rs. ${Number(mode === "receipt" ? receipt_amount_paid : amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(3), { align: "right" });
      doc.setTextColor(0);
      doc.text(`Due Amount: Rs. ${Number(patientPayablePrint - amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(4), { align: "right" });
    }

    doc.setTextColor(0);

    const amountForWordsPrint =
      mode === "receipt"
        ? Math.floor(Number(receipt_amount_paid || 0))
        : Math.floor(Number(patientPayablePrint || 0));
    const amountWords = `${numberToWords(amountForWordsPrint)} Rupees Only`;

    const receivedPrint = "Received with thanks: ";
    const thanksRightEdgePrint =
      narrow
        ? Math.min(amountWordsRightEdge + 6, RIGHT_X - 4)
        : RIGHT_X;
    const {
      thanksFs: thanksFsPrint,
      textWidth: twPrint,
      wrappedAmount: wrappedPrintThanks,
      lineHeight: lhPrint,
    } = getThanksInWordsLayout(
      doc,
      receivedPrint,
      amountWords,
      thanksRightEdgePrint,
      narrow,
    );
    const thanksHPrint = 6 + wrappedPrintThanks.length * lhPrint;

    const summaryEndYPrint = narrow ? sumYPrint(4) : currentY + 21;
    const gapAfterSummaryPrint = narrow ? 5 : 7;
    let thanksYPrint = summaryEndYPrint + gapAfterSummaryPrint;
    const footerReservePrint = narrow ? 6 : 22;
    if (thanksYPrint + thanksHPrint + footerReservePrint > PAGE_HEIGHT) {
      doc.addPage();
      thanksYPrint = withLetterhead ? (narrow ? 16 : 20) : (narrow ? 16 : 30);
    }

    doc.setFontSize(thanksFsPrint);
    doc.setFont("helvetica", "normal");
    doc.text(receivedPrint, labelX, thanksYPrint);
    doc.setFont("helvetica", "bold");
    doc.text(wrappedPrintThanks, labelX + twPrint, thanksYPrint);
    const lastThanksY =
      thanksYPrint + (wrappedPrintThanks.length - 1) * lhPrint;

    const noteY = lastThanksY + (narrow ? 5.5 : 9);
    const signatureYPrint = narrow ? thanksYPrint : noteY;
    doc.setFontSize(narrow ? 5.5 : 8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Note: This is a digitally generated ${mode === "receipt" ? "receipt" : "invoice"}.`,
      labelX,
      noteY,
    );

    const signatoryFontSizePrint = narrow ? Math.max(5, 8 - 2) : 8;
    const signatoryLineStepPrint = narrow ? 6.75 : 9;
    doc.setFontSize(signatoryFontSizePrint);
    doc.setFont("helvetica", "bold");
    doc.text("Authorised Signatory", RIGHT_X, signatureYPrint, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.text(getSignatoryLine(), RIGHT_X, signatureYPrint + signatoryLineStepPrint, { align: "right" });

    if (isA5Print) {
      toast.info("For best A5 output: choose Paper Size A5 and Scale = Fit to printable area.");
    }
    window.open(`${doc.output("bloburl")}#zoom=page-fit`, "_blank");
  };

  const handleHomeButton = () => {
    history.push("/doctor/invoice");
  };

  if (!data) return null;

  const normalizedItemsForUI = itemState.map((it) => normalizeItem(it));

  const summaryUi = getInvoiceSummaryFromItems(normalizedItemsForUI, data);
  const displayItemsForUI = mergeDisplayDiscounts(normalizedItemsForUI, summaryUi);
  const billAmountUi =
    summaryUi.billAmount > 0 ? summaryUi.billAmount : safeNumber(getFinalTotal(data));
  const totalDiscountAmountUi =
    summaryUi.billAmount > 0 ? summaryUi.totalDiscountAmount : 0;
  const FINAL_TOTAL_UI =
    summaryUi.billAmount > 0 ? summaryUi.patientPayable : safeNumber(getFinalTotal(data));

  const invoiceTax = safeNumber(data?.tax ?? 0);

  // Support payment_details (get_all_invoices), payments[], or single payment
  const paymentsList = Array.isArray(data?.payment_details)
    ? data.payment_details
    : Array.isArray(data?.payments)
      ? data.payments
      : data?.payment
        ? [data.payment]
        : [];
  const payment = paymentsList[0] || data?.payment || data?.payments?.[0] || {};
  const payment_date = data?.payment_date || payment?.created_at || "N/A";
  const receipt_no = data?.receipt_no || payment?.receipt_number || "N/A";
  const payment_mode = data?.payment_mode || payment?.mode_of_payment || "N/A";
  const ref_no = data?.ref_no || payment?.transaction_ref_no || "N/A";
  let amount_paid = 0;
  if (paymentsList.length > 0) {
    amount_paid = paymentsList.reduce(
      (sum, p) => sum + safeNumber(p?.payment_amount ?? 0),
      0
    );
  } else {
    amount_paid = safeNumber(data?.amount_paid ?? data?.partial_payment ?? payment?.payment_amount ?? 0);
  }
  const matchedReceiptPaymentUi =
    paymentsList.find((p) => (p?.receipt_number ?? "") === receipt_no) ||
    paymentsList[paymentsList.length - 1] ||
    payment;
  const receipt_amount_paid_ui = safeNumber(
    matchedReceiptPaymentUi?.payment_amount ??
    data?.amount_paid ??
    data?.partial_payment ??
    0,
  );

  const pStatus = (data?.payment_status || "UNPAID").toUpperCase();
  const balance = safeNumber(FINAL_TOTAL_UI - amount_paid);
  const displayStatusBadge = balance > 0.01 ? "DUE" : "PAID";
  const isActuallyPaid = balance <= 0.01;
  const hasPayment = amount_paid > 0.01;

  // Invoice mode: Only download if fully paid (balance is 0)
  // Receipt mode: Download if at least something is paid
  const canDownloadPDF = mode === "invoice" ? isActuallyPaid : hasPayment;

  return (
    <>
      {permissionLoading ? null : (
        <Card style={{ marginBottom: "16px" }}>
          {/* Main Card */}
          <Card
            style={{
              width: "95%",
              height: "100%",
              padding: "16px",
              marginLeft: "40px",
              marginTop: "1%",
            }}
          >
            <div id="invoice-template">
              <Grid
                container
                style={{
                  padding: "50px",
                  fontSize: "18px",
                  lineHeight: "25px",
                  paddingBottom: "20px",
                }}
              >
                {/* LEFT SECTION */}
                <Grid item xs={7}>
                  <div
                    style={{
                      display: "grid",
                      rowGap: "2px",
                      lineHeight: 1.35,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: 0,
                        lineHeight: 1.2,
                      }}
                    >
                      <div style={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {mode === "receipt" ? "Bill Cum Receipt" : "Bill of Supply"}
                      </div>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          color: displayStatusBadge === "PAID" ? "#065f46" : "#991b1b",
                          backgroundColor:
                            displayStatusBadge === "PAID" ? "#d1fae5" : "#fee2e2",
                        }}
                      >
                        {displayStatusBadge}
                      </Box>
                    </div>
                    <div>
                      <b>Name:</b>{" "}
                      {data?.patient_name ||
                        data?.patientName ||
                        displayPatient?.name ||
                        "N/A"}
                    </div>
                    <div>
                      <b>Age/Gender:</b> {formatAgeGenderLine(displayPatient, data)}
                    </div>
                    <div>
                      <b>Contact No:</b> {formatContactNumbers(displayPatient, data)}
                    </div>

                    {/* UHID (comment only - future use) */}
                    {/*
                  <div>
                    <b>UHID:</b> {data?.uhid || patient?.uhid || "N/A"}
                  </div>
                  */}

                    <div
                      style={{
                        maxWidth: "420px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      <b>Address:</b>{" "}
                      {data?.address ||
                        displayPatient?.address ||
                        [
                          displayPatient?.address_line,
                          displayPatient?.locality,
                          displayPatient?.city || displayPatient?.district,
                          displayPatient?.state,
                          displayPatient?.pincode
                        ].filter(v => v && !["null", "undefined", "n/a"].includes(v.toString().toLowerCase())).join(", ") ||
                        "N/A"}
                    </div>
                    <div>
                      <b>Referred by:</b>{" "}
                      {formatReferredBy(data?.referred_by || displayPatient?.referred_by)}
                    </div>
                  </div>
                </Grid>

                {/* RIGHT SECTION */}
                <Grid
                  item
                  xs={5}
                  style={{ textAlign: "left", paddingLeft: "230px" }}
                >
                  <div style={{ display: "grid", rowGap: "4px" }}>
                    <div>
                      <b>{mode === "receipt" ? "Receipt No:" : "Bill No:"}</b> {mode === "receipt" ? receipt_no : (data?.invoice_number || "N/A")}
                    </div>

                    <div>
                      <b>Pat ID:</b>{" "}
                      {displayPatient?.patient_id || data?.patient_id || data?.patientId || "N/A"}
                    </div>

                    <div>
                      <b>Date:</b> {formatDate(getInvoiceDate(data))}
                    </div>

                    {/* Prescribed Doctor (comment only - future use) */}
                    {/*
                  <div>
                    <b>Prescribed Doctor:</b> --
                  </div>
                  */}

                    {/* Auth Code (comment only - future use) */}
                    {/*
                  <div>
                    <b>Emp No/Auth Code:</b> --
                  </div>
                  */}

                    <div>
                      <b>Center:</b> {data?.center || data?.facility_name || userInfo?.facility_name || sessionStorage.getItem("facility_display_name") || "N/A"}
                    </div>

                    <div style={{ maxWidth: "420px", wordBreak: "break-word" }}>
                      <b>Center Address:</b>{" "}
                      {data?.center_address || data?.facility_address || buildFacilityAddress(userInfo) || "N/A"}
                    </div>
                  </div>
                </Grid>
              </Grid>

              {/* Invoice Card */}
              <Card style={{ height: "auto" }}>
                {/* Invoice Header */}
                <Grid
                  container
                  style={{
                    backgroundColor: "#C7F4EF",
                    padding: "16px 20px",
                    alignItems: "center",
                    borderRadius: "8px 8px 0 0",
                  }}
                >
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight="bold">
                      {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} textAlign="right">
                    {collateralImage && (
                      <img
                        src={`data:image/png;base64,${collateralImage}`}
                        height="40"
                        alt="logo"
                      />
                    )}
                  </Grid>
                </Grid>

                {/* Table — same font as patient header (18px) so payment rows can match */}
                <Grid
                  item
                  xs={12}
                  style={{
                    padding: "20px",
                    display: "flex",
                    justifyContent: "left",
                    width: "100vw",
                    fontSize: "18px",
                    lineHeight: "25px",
                  }}
                >
                  <div style={{ width: "90%" }}>
                    {/* TABLE HEADER */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2.2fr 0.9fr 0.7fr 1fr 1fr 1fr 1fr 0.8fr 1fr",
                        columnGap: 1,
                        fontWeight: "bold",
                        padding: "10px 0",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      {/* Department */}
                      <Box>Service Description</Box>
                      <Box sx={{ textAlign: "center" }}>SAC Code</Box>
                      <Box sx={{ textAlign: "center" }}>Qty</Box>
                      <Box sx={{ textAlign: "right" }}>Price (₹)</Box>
                      <Box sx={{ textAlign: "right" }}>Gross Value</Box>
                      <Box sx={{ textAlign: "right" }}>Discount %</Box>
                      <Box sx={{ textAlign: "right" }}>Discount (₹)</Box>
                      <Box sx={{ textAlign: "right" }}>Tax</Box>
                      <Box sx={{ textAlign: "right" }}>Amount (₹)</Box>
                    </Box>

                    {/* TABLE ROWS */}
                    {displayItemsForUI.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "2.2fr 0.9fr 0.7fr 1fr 1fr 1fr 1fr 0.8fr 1fr",
                          columnGap: 1,
                          borderBottom: "1px solid #eee",
                          padding: "10px 0",
                        }}
                      >
                        {/* Department */}
                        <Box>{item.item_name || "--"}</Box>
                        <Box sx={{ textAlign: "center" }}>--</Box>
                        <Box sx={{ textAlign: "center" }}>{item.quantity}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.item_price)}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.gross)}</Box>
                        <Box sx={{ textAlign: "right" }}>{formatMoney(item.discount_percent)}%</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.discount_amount)}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.tax || invoiceTax || 0)}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.subtotal)}</Box>
                      </Box>
                    ))}
                  </div>
                </Grid>

                {/* Total + Payment */}
                <Grid
                  container
                  style={{
                    padding: "20px",
                    marginTop: "20px",
                    alignItems: "flex-start",
                    fontSize: "18px",
                    lineHeight: "25px",
                  }}
                >
                  {/* LEFT */}
                  <Grid item xs={7}>
                    {mode === "invoice" ? (
                      <>
                        <Grid
                          container
                          style={{
                            fontWeight: "bold",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "6px",
                          }}
                        >
                          <Grid item xs={3}>
                            Payment Settlement
                          </Grid>
                          <Grid item xs={2}>
                            Receipt No
                          </Grid>
                          <Grid item xs={2}>
                            Mode
                          </Grid>
                          <Grid item xs={3}>
                            Ref.No
                          </Grid>
                          <Grid item xs={2} style={{ display: "flex", justifyContent: "flex-end" }}>
                            Amount
                          </Grid>
                        </Grid>

                        {paymentsList.length > 0 ? (
                          paymentsList.map((p, idx) => (
                            <Grid
                              key={p?.payment_id ?? p?.receipt_number ?? idx}
                              container
                              style={{ paddingTop: "6px" }}
                            >
                              <Grid item xs={3}>
                                {formatDate(
                                  p?.created_at || data?.payment_date || "N/A",
                                )}
                              </Grid>
                              <Grid item xs={2}>
                                {p?.receipt_number ?? data?.receipt_no ?? "N/A"}
                              </Grid>
                              <Grid item xs={2}>
                                {p?.mode_of_payment ?? data?.payment_mode ?? "N/A"}
                              </Grid>
                              <Grid
                                item
                                xs={3}
                                style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                              >
                                {p?.transaction_ref_no ?? data?.ref_no ?? "N/A"}
                              </Grid>
                              <Grid
                                item
                                xs={2}
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "flex-start",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                ₹{formatMoney(p?.payment_amount ?? 0)}
                              </Grid>
                            </Grid>
                          ))
                        ) : (
                          <Grid container style={{ paddingTop: "6px" }}>
                            <Grid item xs={3}>
                              {formatDate(payment_date)}
                            </Grid>
                            <Grid item xs={2}>
                              {receipt_no}
                            </Grid>
                            <Grid item xs={2}>
                              {payment_mode}
                            </Grid>
                            <Grid
                              item
                              xs={3}
                              style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                            >
                              {ref_no}
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "flex-start",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              ₹{formatMoney(amount_paid)}
                            </Grid>
                          </Grid>
                        )}
                      </>
                    ) : (
                      <>
                        <Grid
                          container
                          style={{
                            fontWeight: "bold",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "6px",
                          }}
                        >
                          <Grid item xs={4}>
                            Mode
                          </Grid>
                          <Grid item xs={4}>
                            Ref.No
                          </Grid>
                          <Grid item xs={4}>
                            Amount
                          </Grid>
                        </Grid>

                        {paymentsList.length > 0 ? (
                          paymentsList.map((p, idx) => (
                            <Grid
                              key={p?.payment_id ?? p?.receipt_number ?? idx}
                              container
                              style={{ paddingTop: "6px" }}
                            >
                              <Grid item xs={4}>
                                {p?.mode_of_payment ?? data?.payment_mode ?? "N/A"}
                              </Grid>
                              <Grid item xs={4}>
                                {p?.transaction_ref_no ?? data?.ref_no ?? "N/A"}
                              </Grid>
                              <Grid item xs={4}>
                                ₹{formatMoney(p?.payment_amount ?? 0)}
                              </Grid>
                            </Grid>
                          ))
                        ) : (
                          <Grid container style={{ paddingTop: "6px" }}>
                            <Grid item xs={4}>
                              {payment_mode}
                            </Grid>
                            <Grid item xs={4}>
                              {ref_no}
                            </Grid>
                            <Grid item xs={4}>
                              ₹{formatMoney(amount_paid)}
                            </Grid>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>

                  {/* RIGHT */}
                  <Grid
                    item
                    xs={5}
                    style={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <div style={{ textAlign: "right" }}>
                      {mode === "invoice" ? (
                        <>
                          <Typography>
                            Bill Amount: ₹{formatMoney(billAmountUi)}
                          </Typography>
                          <Typography>
                            Discount Amount: ₹{formatMoney(totalDiscountAmountUi)}
                          </Typography>
                          <Typography>
                            Patient Payable: ₹{formatMoney(FINAL_TOTAL_UI)}
                          </Typography>
                          <Typography
                            fontWeight="bold"
                            style={{ marginTop: "8px", color: "#0e3f66" }}
                          >
                            Amount Paid: ₹{formatMoney(mode === "receipt" ? receipt_amount_paid_ui : amount_paid)}
                          </Typography>
                          <Typography
                            fontWeight="bold"
                            style={{ marginTop: "8px" }}
                          >
                            Due Amount: ₹{formatMoney(FINAL_TOTAL_UI - amount_paid)}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography>
                            Bill Amount: ₹{formatMoney(billAmountUi)}
                          </Typography>
                          <Typography>
                            Discount Amount: ₹{formatMoney(totalDiscountAmountUi)}
                          </Typography>
                          <Typography>
                            Patient Payable: ₹{formatMoney(FINAL_TOTAL_UI)}
                          </Typography>
                          <Typography
                            fontWeight="bold"
                            style={{ marginTop: "8px", color: "#0e3f66" }}
                          >
                            Amount Paid: ₹{formatMoney(mode === "receipt" ? receipt_amount_paid_ui : amount_paid)}
                          </Typography>
                          <Typography
                            fontWeight="bold"
                            style={{ marginTop: "8px" }}
                          >
                            Due Amount: ₹{formatMoney(FINAL_TOTAL_UI - amount_paid)}
                          </Typography>
                        </>
                      )}

                      {/* Moved Amount Paid inside mode-specific blocks */}
                    </div>
                  </Grid>
                </Grid>

                {/* Footer Terms */}
                <Grid
                  container
                  style={{ padding: "20px", alignItems: "flex-end" }}
                >
                  <Grid item xs={8}>
                    <Typography variant="subtitle1">
                      Received with thanks:{" "}
                      <b>
                        {numberToWords(
                          Math.floor(
                            Number(
                              mode === "receipt"
                                ? receipt_amount_paid_ui || 0
                                : FINAL_TOTAL_UI || 0,
                            ),
                          ),
                        )}{" "}
                        Rupee Only
                      </b>
                    </Typography>
                  </Grid>

                  <Grid item xs={4} style={{ textAlign: "right" }}>
                    <Box>
                      <Typography variant="subtitle1" component="div" fontWeight="bold">
                        Authorised Signatory
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        component="div"
                        sx={{ mt: 1.5 }}
                      >
                        {getSignatoryLine()}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Grid
                  container
                  style={{ padding: "10px 20px", alignItems: "center" }}
                >
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Note: This is a digitally generated {mode === "receipt" ? "receipt" : "invoice"}.
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </div>

            <Grid
              container
              justifyContent="center"
              style={{ marginTop: "24px" }}
            >
              <Grid item xs={12} sm={8} md={4}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id="invoice-paper-size-label">
                    Paper size (PDF and print)
                  </InputLabel>
                  <Select
                    labelId="invoice-paper-size-label"
                    id="invoice-paper-size"
                    label="Paper size (PDF and print)"
                    value={invoicePaperSize}
                    onChange={(e) => setInvoicePaperSize(e.target.value)}
                  >
                    <MenuItem value="a4">A4 (210 × 297 mm)</MenuItem>
                    <MenuItem value="a5">A5 (148 × 210 mm)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* A5 printing tip (hidden)
            {invoicePaperSize === "a5" && (
              <Grid container justifyContent="center" style={{ marginTop: "16px" }}>
                <Grid item xs={12} sm={10} md={8}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f0fdfa",
                      border: "1px solid #5eead4",
                      borderRadius: "8px",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="subtitle2" color="#0f766e" fontWeight="bold">
                      A5 Printing Tip:
                    </Typography>
                    <Typography variant="body2" color="#134e4a">
                      In the print dialog, please set <b>Paper Size to A5</b> and <b>Scale to 100%</b> (no scaling/fit to page) for perfect results.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            */}

            {/* Buttons */}
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={4}
              style={{ marginTop: "16px" }}
            >
              <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                <button
                  type="button"
                  onClick={
                    canDownloadPDF
                      ? (e) => setDownloadMenuAnchorEl(e.currentTarget)
                      : undefined
                  }
                  disabled={!canDownloadPDF}
                  style={{
                    width: "100%",
                    padding: "18px 0",
                    backgroundColor: canDownloadPDF ? "#21cdc0" : "#ccc",
                    color: canDownloadPDF ? "#ffffffff" : "#666",
                    borderRadius: "10px",
                    fontSize: "22px",
                    border: "none",
                    cursor: canDownloadPDF ? "pointer" : "not-allowed",
                  }}
                >
                  DOWNLOAD PDF
                </button>
                <Menu
                  anchorEl={downloadMenuAnchorEl}
                  open={Boolean(downloadMenuAnchorEl)}
                  onClose={() => setDownloadMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  PaperProps={{
                    style: {
                      width: downloadMenuAnchorEl
                        ? downloadMenuAnchorEl.offsetWidth
                        : undefined,
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleDownload(true);
                      setDownloadMenuAnchorEl(null);
                    }}
                  >
                    {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"} with Letterhead
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleDownload(false);
                      setDownloadMenuAnchorEl(null);
                    }}
                  >
                    {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"} without
                    Letterhead
                  </MenuItem>
                </Menu>
              </Grid>

              <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                <button
                  type="button"
                  onClick={
                    canDownloadPDF
                      ? (e) => setPrintMenuAnchorEl(e.currentTarget)
                      : undefined
                  }
                  disabled={!canDownloadPDF}
                  style={{
                    width: "100%",
                    padding: "18px 0",
                    backgroundColor: canDownloadPDF ? "#21cdc0" : "#ccc",
                    color: canDownloadPDF ? "#ffffffff" : "#666",
                    borderRadius: "10px",
                    fontSize: "22px",
                    border: "none",
                    cursor: canDownloadPDF ? "pointer" : "not-allowed",
                  }}
                >
                  PRINT
                </button>
                <Menu
                  anchorEl={printMenuAnchorEl}
                  open={Boolean(printMenuAnchorEl)}
                  onClose={() => setPrintMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  PaperProps={{
                    style: {
                      width: printMenuAnchorEl
                        ? printMenuAnchorEl.offsetWidth
                        : undefined,
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handlePrint(true);
                      setPrintMenuAnchorEl(null);
                    }}
                  >
                    {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"} with Letterhead
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handlePrint(false);
                      setPrintMenuAnchorEl(null);
                    }}
                  >
                    {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"} without
                    Letterhead
                  </MenuItem>
                </Menu>
              </Grid>

              <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                <button
                  type="button"
                  onClick={handleHomeButton}
                  style={{
                    width: "100%",
                    padding: "18px 0",
                    backgroundColor: "#21cdc0",
                    color: "#fff",
                    borderRadius: "10px",
                    fontSize: "22px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  GO TO HOME
                </button>
              </Grid>
            </Grid>
          </Card>
        </Card>
      )}
    </>
  );
};

export default ViewInvoice;